import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import DateRange from "shared/helpers/DateRange";
import { isSocialBookmarkStream, isSocialStream } from "shared/helpers/stream";
import type {
  ChartIndex,
  SavedChartSetting,
} from "shared/modules/Charts/types";
import {
  ApplicationRecord,
  objectDirtyChecker,
} from "shared/services/spraypaint";
import type { Maybe, Nullable, Stream } from "shared/types";
import { type SocialMentionType } from "shared/types/mentions";

import type AIVisibilityProject from "./AIVisibilityProject";
import type Favorite from "./Favorite";
import type User from "./User";
import type UserAccessible from "./UserAccessible";
import type ViewedItem from "./ViewedItem";

@Model()
class InstantInsightsReport extends ApplicationRecord {
  static jsonapiType = "instant_insights_reports";

  @Attr({ dirtyChecker: objectDirtyChecker }) advancedFilters: Record<
    string,
    any
  >;

  @Attr() after: number;

  @HasOne() aiVisibilityProject: Maybe<AIVisibilityProject>;

  @Attr() before: number;

  @Attr({ persist: false }) canModify: boolean;

  @Attr({ persist: false }) canShare: boolean;

  @Attr({ dirtyChecker: objectDirtyChecker }) chartSettings: ChartIndex;

  @Attr() collapseSyndicated: boolean;

  @Attr({ persist: false }) createdAt: string;

  @Attr() description: string;

  @HasOne() favorite: Favorite;

  @Attr() magazineContent: boolean;

  @Attr() name: string;

  @Attr({ dirtyChecker: objectDirtyChecker })
  newChartSettings: SavedChartSetting[];

  @Attr() onlineContent: boolean;

  @Attr({ persist: false }) organisationBrandId: number;

  @Attr() organisationTeamId: number;

  @Attr() podcastContent: boolean;

  @Attr() printContent: boolean;

  @Attr({ persist: false }) publicAccessKey: Nullable<string>;

  @Attr() radioContent: boolean;

  @Attr() rangeLabel: Nullable<string>;

  @Attr({ dirtyChecker: objectDirtyChecker }) selectedCharts: any[];

  @Attr() selectedSocialContent: SocialMentionType[];

  @Attr() socialContent: boolean;

  @Attr({ dirtyChecker: objectDirtyChecker }) streamIds: number[];

  @Attr({ dirtyChecker: () => false, persist: false })
  streamsData: Stream[];

  @Attr() summary: string;

  @Attr() tvContent: boolean;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo() user: User;

  @HasMany() userAccessibles: UserAccessible[];

  @Attr() userId: number;

  @HasMany({ name: "target" }) viewedItems: ViewedItem[];

  static withDashboardScope() {
    return InstantInsightsReport.withUserAccessibles().includes([
      "viewedItems",
      "user",
      "favorite",
      "aiVisibilityProject",
    ]);
  }

  static withUserAccessibles() {
    return InstantInsightsReport.selectExtra([
      "canModify",
      "canShare",
    ]).includes(["userAccessibles"]);
  }

  absoluteRange() {
    const { after, before } = this;

    if (this.before && this.after) {
      return new DateRange({ after, before } as DateRange);
    }

    return null;
  }

  isShared() {
    return (
      this.userAccessibles?.some((record) => record.userId !== this.userId) ||
      Boolean(this.publicAccessKey)
    );
  }

  isSharedBy(userId: number) {
    return (
      this.userId === userId &&
      this.userAccessibles?.some((record) => record.userId !== userId)
    );
  }

  relativeRange() {
    if (this.rangeLabel) {
      return DateRange.rangeForLabel(this.rangeLabel);
    }

    return null;
  }

  selectedContent() {
    return [
      ...(this.onlineContent ? ["Online"] : []),
      ...(this.printContent ? ["Print"] : []),
      ...(this.magazineContent ? ["Magazine"] : []),
      ...(this.tvContent ? ["TV"] : []),
      ...(this.radioContent ? ["Radio"] : []),
      ...(this.socialContent ? ["Social"] : []),
      ...(this.podcastContent ? ["Podcast"] : []),
    ];
  }

  sharedSocialStreams() {
    const sharedStreams: Stream[] = this.streamsData || [];

    return sharedStreams.filter(
      (item) => isSocialStream(item) || isSocialBookmarkStream(item)
    );
  }

  sharedStreams() {
    const sharedStreams: Stream[] = this.streamsData || [];

    const sharedSocialStreamIds = this.sharedSocialStreams().map(
      (stream) => stream.id
    );

    return sharedStreams.filter(
      (stream) => !sharedSocialStreamIds.includes(stream.id)
    );
  }
}

export default InstantInsightsReport;
